<template>
  <v-dialog :value="value" max-width="860" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="quick__title">
        Стандартні відповіді і меню бота
        <v-spacer></v-spacer>
        <v-btn icon aria-label="Закрити" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <p class="grey--text">
          Кожна відповідь стає кнопкою в меню бота (команда /menu і перше повідомлення після «Почати»): людина натискає кнопку і одразу отримує текст.
          Щоб зробити гілку, вкажіть «Всередині кнопки»: тоді кнопка з'явиться після натискання на ту, що вище.
          Ці ж тексти можна вставити у відповідь у чаті кнопкою поруч із полем.
        </p>

        <ul v-if="tree.length" class="quick__tree">
          <li v-for="row in tree" :key="row.item.id" class="quick__row" :style="{ paddingLeft: row.depth * 24 + 'px' }">
            <span class="quick__name">
              <v-icon small :color="row.item.in_menu ? 'primary' : 'grey'">{{ row.depth ? "mdi-subdirectory-arrow-right" : "mdi-gesture-tap-button" }}</v-icon>
              <b>{{ row.item.title }}</b>
              <span v-if="!row.item.in_menu" class="grey--text caption">лише для CRM</span>
              <span class="quick__text">{{ row.item.text }}</span>
            </span>
            <span class="quick__actions">
              <v-btn x-small text color="primary" @click="startNew(row.item.id)">+ всередину</v-btn>
              <v-btn x-small text color="primary" @click="startEdit(row.item)">Змінити</v-btn>
              <v-btn x-small text color="red" @click="remove(row.item)">Видалити</v-btn>
            </span>
          </li>
        </ul>
        <p v-else-if="loaded" class="grey--text">Відповідей ще немає.</p>

        <v-form v-if="form" ref="form" class="quick__form" @submit.prevent="save">
          <h3 class="quick__form-title">{{ form.id ? "Змінити відповідь" : "Нова відповідь" }}</h3>
          <v-text-field v-model="form.title" label="Текст кнопки" outlined dense counter="60" maxlength="60" :rules="[required]"></v-text-field>
          <v-textarea v-model="form.text" label="Відповідь, яку отримає людина" outlined dense auto-grow rows="3" counter="3500" maxlength="3500" :rules="[required]"></v-textarea>
          <div class="quick__form-line">
            <v-select v-model="form.parent_id" :items="parentOptions" label="Всередині кнопки" outlined dense hide-details class="quick__parent"></v-select>
            <v-text-field v-model.number="form.sort" type="number" min="0" label="Порядок" outlined dense hide-details class="quick__sort"></v-text-field>
            <v-switch v-model="form.in_menu" label="Показувати в меню бота" hide-details class="mt-0 pt-0"></v-switch>
          </div>
          <p v-if="error" class="red--text caption mt-2 mb-0">{{ error }}</p>
          <div class="quick__form-buttons">
            <v-btn depressed color="primary" type="submit" :loading="saving">Зберегти</v-btn>
            <v-btn text @click="form = null">Скасувати</v-btn>
          </div>
        </v-form>
        <v-btn v-else depressed color="primary" class="mt-4" @click="startNew(null)"><v-icon left small>mdi-plus</v-icon>Додати відповідь</v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiClient from "@/libs/http-client";

export default {
  name: "CrmQuickReplies",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      items: [],
      loaded: false,
      form: null,
      saving: false,
      error: "",
    };
  },
  computed: {
    // depth-first: every branch right under its button
    tree() {
      const rows = [];
      const walk = (parentId, depth) => {
        this.items
          .filter((item) => (item.parent_id || null) === parentId)
          .sort((a, b) => a.sort - b.sort || a.id - b.id)
          .forEach((item) => {
            rows.push({ item, depth });
            walk(item.id, depth + 1);
          });
      };
      walk(null, 0);
      return rows;
    },
    // a button cannot go inside itself or its own branch
    parentOptions() {
      const blocked = new Set();
      if (this.form && this.form.id) {
        const mark = (id) => {
          blocked.add(id);
          this.items.filter((item) => item.parent_id === id).forEach((item) => mark(item.id));
        };
        mark(this.form.id);
      }
      return [{ text: "Головне меню", value: null }].concat(
        this.tree
          .filter((row) => !blocked.has(row.item.id))
          .map((row) => ({ text: `${"— ".repeat(row.depth)}${row.item.title}`, value: row.item.id }))
      );
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
  },
  methods: {
    required(value) {
      return !!(value && String(value).trim()) || "Заповніть поле";
    },
    load() {
      return ApiClient.get("api/admin/crm/telegram-quick-replies").then((response) => {
        this.items = response.data.data;
        this.loaded = true;
      });
    },
    startNew(parentId) {
      const siblings = this.items.filter((item) => (item.parent_id || null) === parentId);
      this.form = { id: null, title: "", text: "", parent_id: parentId, sort: siblings.length ? Math.max(...siblings.map((item) => item.sort)) + 1 : 0, in_menu: true };
      this.error = "";
    },
    startEdit(item) {
      this.form = { ...item };
      this.error = "";
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.error = "";
      const body = { title: this.form.title, text: this.form.text, parent_id: this.form.parent_id, sort: this.form.sort || 0, in_menu: this.form.in_menu };
      const request = this.form.id
        ? ApiClient.put(`api/admin/crm/telegram-quick-replies/${this.form.id}`, body)
        : ApiClient.post("api/admin/crm/telegram-quick-replies", body);
      request
        .then(() => {
          this.form = null;
          return this.load();
        })
        .then(() => this.$emit("changed"))
        .catch((error) => {
          this.error = (error && error.message) || "Не вдалося зберегти";
        })
        .finally(() => {
          this.saving = false;
        });
    },
    remove(item) {
      const inside = this.items.some((row) => row.parent_id === item.id);
      if (!window.confirm(inside ? `Видалити «${item.title}» разом із кнопками всередині?` : `Видалити «${item.title}»?`)) return;
      ApiClient.delete(`api/admin/crm/telegram-quick-replies/${item.id}`)
        .then(() => this.load())
        .then(() => this.$emit("changed"));
    },
  },
};
</script>

<style lang="scss" scoped>
.quick {
  &__title {
    word-break: normal;
  }
  &__tree {
    margin: 0 0 8px;
    padding: 0;
    list-style: none;
  }
  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px 12px;
    padding-top: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
  }
  &__name {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    flex: 1 1 320px;
    min-width: 0;
  }
  &__text {
    flex: 1 1 100%;
    overflow: hidden;
    color: #666;
    font-size: 13px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
  }
  &__form {
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #e3eef0;
    border-radius: 8px;
  }
  &__form-title {
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 500;
  }
  &__form-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
  }
  &__parent {
    flex: 1 1 260px;
  }
  &__sort {
    flex: 0 0 110px;
  }
  &__form-buttons {
    display: flex;
    gap: 8px;
    margin-top: 16px;
  }
}

@media (max-width: 900px) {
  // iOS zooms the page on a smaller field
  .quick ::v-deep input,
  .quick ::v-deep textarea {
    font-size: 16px;
  }
}
</style>
